import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { CoachButton, Eyebrow, Row } from './primitives';
import { useFeatureLabel, useTierLabels } from '../lib/queries';

/**
 * What a member is told when something belongs to a plan they do not hold.
 *
 * The server refuses with a 402 carrying a `feature`, never with words: the
 * copy lives here, so one screen can show the lock in place instead of an
 * error after a request that was always going to fail.
 */
export type ProFeature =
  | 'kala_chat'
  | 'journal_reflection'
  | 'affirmation_generation'
  | 'action_suggestions'
  | 'chart'
  | 'guidance'
  | 'goal_limit'
  | 'daily_messages'
  | 'breathwork'
  | 'frequencies'
  | 'biorhythm'
  | 'karma'
  | 'aura'
  | 'palmistry'
  | 'mantras'
  | 'meditation'
  | 'prompt_library'
  | 'programs'
  | 'library';

type Copy = { title: string; body: string };

/**
 * Written against the plan that opens the feature, named as the tenant named
 * it (`useFeatureLabel`): "Standard", "Premium", "Success Pro"…
 */
const COPY: Record<ProFeature, (plan: string) => Copy> = {
  kala_chat: (plan) => ({
    title: `Master Kala is part of ${plan}`,
    body: `Your daily reading, actions and practices are here on every plan. A conversation with your coach comes with ${plan}.`,
  }),
  journal_reflection: (plan) => ({
    title: `Master Kala reads your journal with ${plan}`,
    body: `${plan} members get a short reflection on what they wrote, in their own coaching style.`,
  }),
  affirmation_generation: (plan) => ({
    title: 'Affirmations written for your goal',
    body: `${plan} writes new affirmations around the goal you are working on. Your own list stays yours either way.`,
  }),
  action_suggestions: (plan) => ({
    title: 'Let your coach plan the next step',
    body: `${plan} suggests the actions most likely to move this goal, and you decide which to keep.`,
  }),
  chart: (plan) => ({
    title: `Your birth chart is part of ${plan}`,
    body: `${plan} opens your full chart reading alongside the practical plan.`,
  }),
  guidance: (plan) => ({
    title: `Today's guidance is part of ${plan}`,
    body: `${plan} adds a daily reading to work alongside your actions.`,
  }),
  goal_limit: (plan) => ({
    title: 'Work on more than one goal',
    body: `Free keeps you to a single goal, which is often the right number. ${plan} lets you hold several at once.`,
  }),
  daily_messages: (plan) => ({
    title: 'More time with Master Kala',
    body: `${plan} raises how many messages you can send each day.`,
  }),
  breathwork: (plan) => ({
    title: `Guided breathwork is part of ${plan}`,
    body: `${plan} opens breathing sessions for calm, focus, energy and sleep, paced by a guide you can follow with your eyes closed.`,
  }),
  frequencies: (plan) => ({
    title: `The frequency library is part of ${plan}`,
    body: `${plan} opens listening sessions for focus, relaxation, sleep and meditation.`,
  }),
  palmistry: (plan) => ({
    title: `The palm reading is part of ${plan}`,
    body: `${plan} reads a photo of your own hand in the traditional way. The photo is read once and never saved.`,
  }),
  aura: (plan) => ({
    title: `The aura reading is part of ${plan}`,
    body: `${plan} opens the reading: your colours, your seven centres and one thing to do about the quietest of them.`,
  }),
  karma: (plan) => ({
    title: `The karma reading is part of ${plan}`,
    body: `${plan} opens the reading: the pattern you fall back on, what you are growing toward, and a practice for it.`,
  }),
  mantras: (plan) => ({
    title: `The mantra library is part of ${plan}`,
    body: `${plan} opens the mantras, what each one says, and a counter to repeat one with.`,
  }),
  meditation: (plan) => ({
    title: `Guided meditation is part of ${plan}`,
    body: `${plan} opens the guided sessions, walked through a step at a time on a timer.`,
  }),
  prompt_library: (plan) => ({
    title: `The prompt library is part of ${plan}`,
    body: `Ask Master Kala anything on any plan. The ready-made prompts, including the scripts for invites and follow-ups, come with ${plan}.`,
  }),
  programs: (plan) => ({
    title: `Guided programs are part of ${plan}`,
    body: 'A program walks you through a week or a month one day at a time: a short read, a question for Master Kala, and one thing to do.',
  }),
  library: (plan) => ({
    title: `The library is part of ${plan}`,
    body: `Videos, audio, pictures and guides collected for you, with Master Kala able to find the right one. It comes with ${plan}.`,
  }),
  biorhythm: (plan) => ({
    title: `Your biorhythm is part of ${plan}`,
    body: `${plan} shows where your physical, emotional and intellectual cycles stand each day, so you can plan around your energy.`,
  }),
};

/** The plan a lock names: the feature's own, or the first paid plan for the two limits that are not features. */
function usePlanFor(feature: ProFeature): string {
  const labels = useTierLabels();
  const own = useFeatureLabel(
    feature === 'goal_limit' || feature === 'daily_messages' ? 'journal_reflection' : feature,
  );
  return feature === 'goal_limit' || feature === 'daily_messages' ? labels.pro : own;
}

/** "{n} more tracks with Standard" under a list whose rest a higher plan holds. */
export function MoreWithPlan({
  count,
  noun,
  nouns,
}: {
  count: number;
  noun?: string;
  nouns?: string;
}) {
  const labels = useTierLabels();
  if (count <= 0) return null;
  const what = noun ? ` ${count === 1 ? noun : (nouns ?? `${noun}s`)}` : '';
  return (
    <Row
      title={`${count} more${what} with ${labels.pro}`}
      meta={`See what ${labels.pro} adds`}
      to="/upgrade"
    />
  );
}

export function ProLock({ feature, compact }: { feature: ProFeature; compact?: boolean }) {
  const plan = usePlanFor(feature);
  const copy = COPY[feature](plan);
  if (compact) {
    return (
      <p className="eyebrow flex items-center gap-2">
        <Lock className="h-3.5 w-3.5 text-gold" aria-hidden />
        {copy.title}.{' '}
        <Link to="/upgrade" className="text-gold underline underline-offset-4">
          See {plan}
        </Link>
      </p>
    );
  }
  return (
    <section className="border-b border-border px-5 py-6">
      <Eyebrow className="mb-2 flex items-center gap-2">
        <Lock className="h-3.5 w-3.5 text-gold" aria-hidden /> {plan}
      </Eyebrow>
      <h2 className="font-display text-[1.35rem] font-semibold leading-snug text-ink">
        {copy.title}
      </h2>
      <p className="mt-2 text-[0.93rem] leading-relaxed text-ink/70 lg:max-w-prose">{copy.body}</p>
      <Link to="/upgrade" className="mt-5 inline-block">
        <CoachButton kind="gold">See what {plan} adds</CoachButton>
      </Link>
    </section>
  );
}
